import * as React from 'react';
import { useState } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { Button, DatePicker, Form, Input, InputNumber, message, Modal, Select } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import OWButton from '../ow_button/OWButton';
import OWUpload from '../OWUpload';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { OWSubTabs } from '../ow_sub_tabs/OWSubTabs';
import { withUserType } from '../withUserType';
import DeliverableWorkOrders from '../deliverable_work_orders/DeliverableWorkOrders';
import DeliverableTasks from '../deliverable_tasks/DeliverableTasks';
import { EmptyState } from '../empty_state/EmptyState';
import { DELIVERABLE_STATUSES } from '../../constants/DeliverableStatuses';
import { PROJECT_BUDGET_TYPES } from '../../constants/ProjectEntities';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	getCurrency,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { normFile, mediaUploadValidation, isVideoMimeType } from '../../utils/ImageUtils';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import {
	deliverablesRestCrudThunksForBuyer,
	deliverablesRestCrudThunksForSupplier,
} from '../../thunks/deliverables_thunks';
import {
	buyerContactsRestCrudThunksForBuyer,
	buyerContactsRestCrudThunksForSupplier,
} from '../../thunks/buyer_contacts_thunks';

const { TextArea } = Input;

const CONTACTS_TC = 'DELIVERABLE_FORM_CONTACTS_AUTOCOMPLETE';
const CONTACTS_PAGE_SIZE = 25;

const contactLabel = (contact) => {
	const name = `${nullSafeGetOrElse('nameGiven', contact, '')} ${nullSafeGetOrElse(
		'nameFamily',
		contact,
		''
	)}`.trim();
	const email = nullSafeGetOrElse('email', contact, '');
	return name ? `${name} (${email})` : email;
};

// Existing gallery -> antd Upload fileList (so edit shows current media).
// Preserve fileType so a re-save keeps videos tagged as videos.
const galleryToFileList = (gallery = []) =>
	gallery.map((item, index) => ({
		uid: `${nullSafeGetOrElse('fileId', item, index)}`,
		name: nullSafeGetOrElse('fileName', item, ''),
		status: 'done',
		fileId: nullSafeGetOrElse('fileId', item, ''),
		fileName: nullSafeGetOrElse('fileName', item, ''),
		fileType: nullSafeGetOrElse('fileType', item, 'image'),
	}));

// antd Upload fileList -> backend GalleryItem[]. New uploads carry a MIME type on
// `file.type`; existing items carry a saved `fileType` — use either to tag video vs image.
const fileListToGallery = (fileList = []) =>
	(fileList || [])
		.map((file) => {
			const fileId = nullSafeGetOrElse(
				'response.data.fileId',
				file,
				nullSafeGetOrElse('fileId', file, '')
			);
			const fileName = nullSafeGetOrElse(
				'response.data.fileName',
				file,
				nullSafeGetOrElse('fileName', file, nullSafeGetOrElse('name', file, ''))
			);
			const fileType =
				nullSafeGetOrElse('fileType', file, null) ||
				(isVideoMimeType(nullSafeGet('type', file)) ? 'video' : 'image');
			return { fileId, fileName, fileType };
		})
		.filter((item) => item.fileId);

// antd Upload fileList -> backend attachments [{ fileId, fileName }].
const fileListToAttachments = (fileList = []) =>
	(fileList || [])
		.map((file) => {
			const fileId = nullSafeGetOrElse(
				'response.data.fileId',
				file,
				nullSafeGetOrElse('fileId', file, '')
			);
			const fileName = nullSafeGetOrElse(
				'response.data.fileName',
				file,
				nullSafeGetOrElse('fileName', file, nullSafeGetOrElse('name', file, ''))
			);
			return { fileId, fileName };
		})
		.filter((item) => item.fileId);

// Build the form's initial values from the (live) deliverable record. The modal is
// mounted fresh each time it opens (parent renders it only while open), so these seed
// the fields once on mount — no visible-watching effect needed.
const buildInitialValues = (deliverable) => ({
	title: nullSafeGetOrElse('name', deliverable, ''),
	assignedTo: nullSafeGetOrElse('assignedTo', deliverable, []),
	dueDate: nullSafeGet('dueDate', deliverable) ? moment(nullSafeGet('dueDate', deliverable)) : null,
	budget: nullSafeGet('budget', deliverable),
	description: nullSafeGetOrElse('description', deliverable, ''),
	images: galleryToFileList(nullSafeGetOrElse('gallery', deliverable, [])),
	attachments: galleryToFileList(nullSafeGetOrElse('attachments', deliverable, [])),
});

// The "New Deliverable" / "Edit Deliverable" modal (Figma 13207-68101).
const DeliverableFormModal = ({
	projectId,
	deliverable,
	onClose,
	onSaved,
	userType,
	currentUser,
	buyerContacts,
	projectBudgetType,
	createDeliverable,
	updateDeliverable,
	fetchContacts,
	fetchMultipleContacts,
}) => {
	const [form] = Form.useForm();
	const isEdit = !!nullSafeGet('id', deliverable);
	// Per-deliverable budget only applies when the project uses individual budgets.
	const showBudget = projectBudgetType === PROJECT_BUDGET_TYPES.individual;

	const [submitting, setSubmitting] = useState(false);
	const [activeTab, setActiveTab] = useState('overview');

	// Block submit while any gallery image or document is still uploading.
	const imagesValue = Form.useWatch('images', form);
	const attachmentsValue = Form.useWatch('attachments', form);
	const isUploading = [...(imagesValue || []), ...(attachmentsValue || [])].some(
		(file) => file && file.status === 'uploading'
	);

	const currency = getCurrency({ currentUser });
	const currencyId = currency ? currency.id : 'USD';
	const uploadHeaders = { 'X-Auth-Token': retrieveCachedUserDetails(['token']).token };
	const uploadUrl = `${getBackendUri()}/api/v1/${userType}/file/upload`;

	const handleSubmit = () => {
		form
			.validateFields()
			.then((values) => {
				setSubmitting(true);
				// Status is intentionally NOT sent — it is backend-calculated.
				// projectId / ids / budget must be numbers: the backend casts these
				// request-map values to numeric types and rejects strings
				// (e.g. reqValueLong("projectId") / optValueBigDecimal("budget")).
				const entity: any = {
					id: nullSafeGet('id', deliverable),
					projectId: Number(projectId),
					name: values.title,
					description: values.description,
					// Backend parses dueDate as a String (DateTime.parse) — send ISO, not epoch.
					dueDate: values.dueDate ? values.dueDate.startOf('day').toISOString() : null,
					budget: values.budget != null ? Number(values.budget) : undefined,
					assignedTo: values.assignedTo,
					workOrderIds: nullSafeGetOrElse('workOrderIds', deliverable, []).map((id) => Number(id)),
					gallery: fileListToGallery(values.images),
					attachments: fileListToAttachments(values.attachments),
				};
				// New deliverables start as Not Started (hardcoded from the FE); status
				// is otherwise backend-managed, so we don't send it on update.
				if (!isEdit) {
					entity.status = DELIVERABLE_STATUSES.notStarted;
				}
				const action = isEdit ? updateDeliverable : createDeliverable;
				const messageKey = 'deliverable-form-op';
				message.loading({
					content: isEdit ? 'Saving deliverable…' : 'Creating deliverable…',
					key: messageKey,
				});
				action(entity)
					.then((saved) => {
						message.success({
							content: isEdit ? 'Deliverable updated' : 'Deliverable created',
							key: messageKey,
							duration: 1.5,
						});
						if (onSaved) {
							onSaved(saved);
						}
						onClose();
					})
					.catch(() => message.error({ content: 'Failed to save deliverable.', key: messageKey }))
					.finally(() => setSubmitting(false));
			})
			.catch(() => {
				// validation errors are surfaced inline by antd
			});
	};

	const linkedCount = nullSafeGetOrElse('workOrderIds', deliverable, []).length;

	return (
		<Modal
			visible
			width={1080}
			title={isEdit ? 'Edit Deliverable' : 'New Deliverable'}
			onCancel={onClose}
			footer={[
				<OWButton key="cancel" type="default" size="large" onClick={onClose}>
					Cancel
				</OWButton>,
				<OWButton
					key="submit"
					type="primary"
					size="large"
					loading={submitting || isUploading}
					onClick={handleSubmit}
				>
					{isEdit ? 'Save' : 'Create Deliverable'}
				</OWButton>,
			]}
		>
			<Form form={form} layout="vertical" initialValues={buildInitialValues(deliverable)}>
				<div className="flex gap-8">
					<div className="w-[320px] shrink-0">
						<Form.Item
							label="Name"
							name="title"
							rules={[{ required: true, message: 'Name is required' }]}
						>
							<Input placeholder="Deliverable name" />
						</Form.Item>
						<Form.Item
							label="Assigned to"
							name="assignedTo"
							rules={[{ required: true, message: 'Assign at least one person' }]}
						>
							<OWAsyncSelect
								mode="multiple"
								placeholder="Select people"
								style={{ width: '100%' }}
								stateSlice={buyerContacts}
								targetCollectionName={CONTACTS_TC}
								valueAccessor={(contact) => nullSafeGetOrElse('email', contact, '')}
								labelAccessor={(contact) => contactLabel(contact)}
								fetchData={(search, targetCollectionName) =>
									fetchContacts({ search }, targetCollectionName)
								}
								fetchMultiple={(ids, targetCollectionName) =>
									fetchMultipleContacts(ids, targetCollectionName)
								}
								renderRecord={(contact) => (
									<Select.Option
										key={nullSafeGetOrElse('email', contact, '')}
										value={nullSafeGetOrElse('email', contact, '')}
									>
										{contactLabel(contact)}
									</Select.Option>
								)}
							/>
						</Form.Item>
						<Form.Item label="Due Date (optional)" name="dueDate">
							<DatePicker format="MMM D, YYYY" style={{ width: '100%' }} />
						</Form.Item>
						{showBudget ? (
							<Form.Item
								label="Budget"
								name="budget"
								rules={[{ required: true, message: 'Budget is required' }]}
							>
								<InputNumber min={0} style={{ width: '100%' }} addonAfter={currencyId} />
							</Form.Item>
						) : null}
					</div>

					<div className="flex-1">
						<OWSubTabs
							tabs={[
								{ key: 'overview', label: 'Overview' },
								{ key: 'tasks', label: 'Tasks' },
								{ key: 'workOrders', label: `Work Orders (${linkedCount})` },
							]}
							activeKey={activeTab}
							onChange={setActiveTab}
							className="mb-4"
						/>
						{activeTab === 'overview' ? (
							<>
								<Form.Item label="Description" name="description">
									{/* TODO: swap for the shared rich-text editor used on WO/notes descriptions */}
									<TextArea rows={6} placeholder="Add a description" />
								</Form.Item>
								<Form.Item
									label="Gallery"
									name="images"
									valuePropName="fileList"
									getValueFromEvent={normFile}
								>
									<OWUpload
										accept="image/*,video/*"
										name="file"
										listType="picture-card"
										multiple
										headers={uploadHeaders}
										action={uploadUrl}
										beforeUpload={mediaUploadValidation}
									>
										<div style={{ display: 'inline' }}>
											<PlusOutlined />
											<div className="ant-upload-text">Upload</div>
										</div>
									</OWUpload>
								</Form.Item>
								<Form.Item
									label="Documents"
									name="attachments"
									valuePropName="fileList"
									getValueFromEvent={normFile}
								>
									<OWUpload name="file" multiple headers={uploadHeaders} action={uploadUrl}>
										<Button icon={<UploadOutlined />}>Upload documents</Button>
									</OWUpload>
								</Form.Item>
							</>
						) : null}
						{activeTab === 'tasks' ? (
							isEdit ? (
								<DeliverableTasks
									projectId={nullSafeGet('projectId', deliverable)}
									deliverableId={nullSafeGet('id', deliverable)}
								/>
							) : (
								<EmptyState
									headline="Save first"
									body={<p>Create the deliverable, then add tasks to it.</p>}
								/>
							)
						) : null}
						{activeTab === 'workOrders' ? (
							isEdit ? (
								<DeliverableWorkOrders deliverable={deliverable} />
							) : (
								<EmptyState
									headline="Save first"
									body={<p>Create the deliverable, then link work orders to it.</p>}
								/>
							)
						) : null}
					</div>
				</div>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => {
	const project = ownProps.projectId ? getEntityById(ownProps.projectId, state.projects) : null;
	return {
		currentUser: state.session.currentUser,
		buyerContacts: state.buyer_contacts,
		// Read the live deliverable by id so link/unlink from inside the modal is
		// reflected on save (no frozen snapshot). Null when creating.
		deliverable: ownProps.deliverableId
			? getEntityById(ownProps.deliverableId, state.deliverables)
			: null,
		// Deliverables only carry their own budget when the project is on
		// "individual" budget; read it off the project keyed by id, not the shared detail slot.
		projectBudgetType: nullSafeGet('budgetType', project),
	};
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	createDeliverable: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? deliverablesRestCrudThunksForSupplier.create(entity)
				: deliverablesRestCrudThunksForBuyer.create(entity)
		),
	updateDeliverable: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? deliverablesRestCrudThunksForSupplier.update(entity)
				: deliverablesRestCrudThunksForBuyer.update(entity)
		),
	fetchContacts: (params, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? buyerContactsRestCrudThunksForSupplier.read(
						{ ...params, limit: CONTACTS_PAGE_SIZE, offset: 0 },
						targetCollectionName
				  )
				: buyerContactsRestCrudThunksForBuyer.read(
						{ ...params, limit: CONTACTS_PAGE_SIZE, offset: 0 },
						targetCollectionName
				  )
		),
	fetchMultipleContacts: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? buyerContactsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: buyerContactsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(DeliverableFormModal));
